/**
 * Launch, Operations & Integration Flags (P20250925-104, P20250925-110)
 *
 * Read-only environment flags for launch, maintenance, checkout, and third-party integrations.
 * These are set at deploy time; never modified at runtime.
 */

// ============================================================================
// LAUNCH & OPERATIONS FLAGS
// ============================================================================

/**
 * LAUNCH_MODE: When true, allows search engine indexing and shows "Live" status
 * Default: false (blocks all indexing until ready)
 */
export function isLaunched(): boolean {
  return import.meta.env.VITE_LAUNCH_MODE === 'true';
}

/**
 * MAINTENANCE_MODE: When true, shows maintenance page to non-admin users
 * Default: false
 */
export function isMaintenanceMode(): boolean {
  return import.meta.env.VITE_MAINTENANCE_MODE === 'true';
}

/**
 * CHECKOUT_DISABLED: When true, disables purchase CTAs with friendly message
 * Default: false
 */
export function isCheckoutDisabled(): boolean {
  return import.meta.env.VITE_CHECKOUT_DISABLED === 'true';
}

/**
 * Get all launch/operations flags as object (for admin display)
 */
export function getLaunchFlags() {
  return {
    LAUNCH_MODE: isLaunched(),
    MAINTENANCE_MODE: isMaintenanceMode(),
    CHECKOUT_DISABLED: isCheckoutDisabled(),
  };
}

// ============================================================================
// INTEGRATION FLAGS (P20250925-110)
// ============================================================================

/**
 * our trusted payment provider environment mode (B20251011-Stack-Switch-Phase01)
 * "sandbox" = test transactions, "live" = real money
 * Default: "sandbox" (SANDBOX_ESCROW defaults to true)
 */
export function getEscrowEnv(): 'sandbox' | 'live' {
  const sandboxEnv = import.meta.env.VITE_SANDBOX_ESCROW || import.meta.env.SANDBOX_ESCROW;
  const legacyEnv = import.meta.env.VITE_ESCROW_ENV;

  if (legacyEnv === 'live') return 'live';
  if (sandboxEnv === 'false') return 'live';

  return 'sandbox';
}

/**
 * Check if in sandbox mode (Phase 01 default)
 */
export function isSandboxEscrow(): boolean {
  return getEscrowEnv() === 'sandbox';
}

/**
 * Check if Escrow is configured (has API key)
 */
export function isEscrowConfigured(): boolean {
  return true;
}

/**
 * Microsoft Clarity - Session recordings and heatmaps
 * Default: false (prod + consent required)
 */
export function isClarityEnabled(): boolean {
  return import.meta.env.VITE_CLARITY_ENABLED === 'true' && import.meta.env.PROD;
}

/**
 * Get Clarity Project ID (public)
 */
export function getClarityId(): string | undefined {
  return import.meta.env.VITE_CLARITY_ID;
}

/**
 * Zoho SalesIQ - Live chat widget
 * Default: false (prod + consent required)
 */
export function isSalesIQEnabled(): boolean {
  return import.meta.env.VITE_SALESIQ_ENABLED === 'true' && import.meta.env.PROD;
}

/**
 * Get SalesIQ Widget ID (public)
 */
export function getSalesIQId(): string | undefined {
  return import.meta.env.VITE_SALESIQ_WIDGET_ID;
}

/**
 * Zoho CRM - Lead capture (server-side)
 * Default: false
 */
export function isZohoCRMEnabled(): boolean {
  return import.meta.env.VITE_ZOHO_CRM_ENABLED === 'true';
}

/**
 * Zoho Desk - Ticket creation (server-side)
 * Default: false
 */
export function isZohoDeskEnabled(): boolean {
  return import.meta.env.VITE_ZOHO_DESK_ENABLED === 'true';
}

/**
 * P20250925-120: Inbox configuration flags
 */
export function isInboxEnabled(): boolean {
  return isZohoDeskEnabled();
}

export function isInboxSendEnabled(): boolean {
  return import.meta.env.VITE_INBOX_SEND_ENABLED === 'true';
}

export function isAutoReplyEnabled(): boolean {
  return import.meta.env.VITE_AUTO_REPLY_SIMPLE === 'true';
}

/**
 * P20250925-121: Ops Alerts configuration flags
 */
export function isAlertsEmailEnabled(): boolean {
  return import.meta.env.VITE_ALERTS_EMAIL_ENABLED === 'true';
}

export function isAlertsWebhookEnabled(): boolean {
  return import.meta.env.VITE_ALERTS_WEBHOOK_ENABLED === 'true';
}

export function getAlertsTo(): string | undefined {
  return import.meta.env.VITE_ALERTS_TO;
}

export function getAlertsWebhookUrl(): string | undefined {
  return import.meta.env.VITE_ALERTS_WEBHOOK_URL;
}

/**
 * P20250925-122: Zoho Books configuration flags
 */
export function isZohoBooksEnabled(): boolean {
  return import.meta.env.VITE_ZOHO_BOOKS_ENABLED === 'true';
}

export function getZohoBooksOrgId(): string | undefined {
  return import.meta.env.VITE_ZOHO_BOOKS_ORG_ID;
}

export function isZohoBooksSandbox(): boolean {
  return import.meta.env.VITE_ZOHO_BOOKS_SANDBOX === 'true';
}

/**
 * Get all integration flags for admin display
 */
export function getIntegrationFlags() {
  return {
    escrow: {
      env: getEscrowEnv(),
      configured: isEscrowConfigured(),
    },
    clarity: {
      enabled: isClarityEnabled(),
      idPresent: !!getClarityId(),
    },
    salesiq: {
      enabled: isSalesIQEnabled(),
      idPresent: !!getSalesIQId(),
    },
    zohoCRM: {
      enabled: isZohoCRMEnabled(),
    },
    zohoDesk: {
      enabled: isZohoDeskEnabled(),
    },
    inbox: {
      enabled: isInboxEnabled(),
      sendEnabled: isInboxSendEnabled(),
      autoReplyEnabled: isAutoReplyEnabled(),
    },
  };
}

/**
 * PRIVACY_BANNER_ENABLED: When false, cookie banner never shows (hard kill switch)
 * Default: false (banner disabled)
 */
export function isPrivacyBannerEnabled(): boolean {
  return import.meta.env.VITE_PRIVACY_BANNER_ENABLED === 'true';
}

/**
 * Get all flags (launch + integrations)
 */
export function getAllIntegrationFlags() {
  return {
    ...getLaunchFlags(),
    ...getIntegrationFlags(),
  };
}
